import React, { useContext, useEffect, useState, useCallback, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Badge,
  IconButton,
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  CircularProgress,
  Button,
  Tooltip,
  Tabs,
  Tab,
} from "@material-ui/core";
import AlternateEmailOutlinedIcon from "@material-ui/icons/AlternateEmailOutlined";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import PersonPinRoundedIcon from "@material-ui/icons/PersonPinRounded";
import { useHistory } from "react-router-dom";

import UserAvatar from "../UserAvatar";
import useUserNotifications from "../../hooks/useUserNotifications";
import { SocketContext } from "../../context/Socket/SocketContext";
import { useDate } from "../../hooks/useDate";
import { i18n } from "../../translate/i18n";
import { stripMentionMarkers } from "../../utils/mentionMarkers";

const useStyles = makeStyles((theme) => ({
  iconButton: {
    color: "white",
    width: 34,
    height: 34,
    padding: 6,
    borderRadius: "50%",
    margin: theme.spacing(0, 0.4),
    position: "relative",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.12)",
    },
  },
  iconButtonLabel: {
    color: "white",
  },
  badge: {
    "& .MuiBadge-badge": {
      backgroundColor: "#f44336 !important",
      color: "white",
      fontWeight: 700,
      boxShadow: "0 2px 4px rgba(244, 67, 54, 0.4)",
      borderRadius: 8,
      fontSize: "0.65rem",
      height: 16,
      minWidth: 16,
      padding: "0 3px",
      right: 3,
      top: 3,
    },
  },
  popoverPaper: {
    width: 380,
    maxHeight: 480,
    overflow: "hidden",
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[2],
    display: "flex",
    flexDirection: "column",
  },
  header: {
    padding: theme.spacing(2),
  },
  headerTitle: {
    fontWeight: 600,
    fontSize: "1rem",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  list: {
    padding: 0,
    overflowY: "auto",
    flex: 1,
    "&::-webkit-scrollbar": { width: "8px" },
    "&::-webkit-scrollbar-track": {
      backgroundColor: theme.palette.background.default,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.action.disabled,
      borderRadius: "4px",
    },
  },
  item: {
    padding: theme.spacing(1.5, 2),
    cursor: "pointer",
    borderBottom: `1px solid ${theme.palette.divider}`,
    transition: "background-color 0.15s ease",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255, 255, 255, 0.06)"
          : "rgba(0, 0, 0, 0.03)",
    },
  },
  unread: {
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    paddingLeft: theme.spacing(1.625),
  },
  content: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    width: "100%",
  },
  avatar: {
    width: 40,
    height: 40,
    flexShrink: 0,
  },
  info: {
    flex: 1,
    minWidth: 0,
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    marginBottom: 4,
    gap: theme.spacing(0.5),
  },
  title: {
    fontWeight: 600,
    fontSize: "0.875rem",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
  },
  time: {
    fontSize: "0.7rem",
    color: theme.palette.text.disabled,
    display: "flex",
    alignItems: "center",
    gap: 4,
    flexShrink: 0,
  },
  body: {
    fontSize: "0.8125rem",
    color: theme.palette.text.secondary,
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
  },
  empty: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(5, 2),
    color: theme.palette.text.secondary,
    minHeight: 180,
  },
  emptyIcon: {
    fontSize: 48,
    marginBottom: theme.spacing(1.5),
    opacity: 0.3,
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2),
  },
  footer: {
    padding: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  tabs: {
    minHeight: 36,
    borderBottom: `1px solid ${theme.palette.divider}`,
    "& .MuiTab-root": {
      minHeight: 36,
      fontSize: "0.75rem",
      textTransform: "none",
    },
  },
  tabLabel: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 76,
    padding: theme.spacing(0, 1),
  },
  tabBadge: {
    "& .MuiBadge-badge": {
      backgroundColor: "#f44336 !important",
      color: "white",
      fontWeight: 700,
      boxShadow: "0 2px 4px rgba(244, 67, 54, 0.4)",
      borderRadius: 8,
      fontSize: "0.65rem",
      height: 16,
      minWidth: 16,
      padding: "0 3px",
      right: -10,
      top: 1,
    },
  },
  typeLabel: {
    fontSize: "0.68rem",
    color: theme.palette.primary.main,
    fontWeight: 600,
    marginRight: theme.spacing(0.5),
  },
}));

const isFollowNotification = (item) =>
  item?.type === "follow_update" || item?.type === "follow_message";

const getNotificationTypeLabel = (item) => {
  if (item?.type === "mention") return "Menção";
  if (item?.type === "follow_message") return "Seguindo • mensagem";
  if (item?.type === "follow_update") return "Seguindo • atualização";
  return "Notificação";
};

const MentionsPopOver = () => {
  const classes = useStyles();
  const history = useHistory();
  const socketManager = useContext(SocketContext);
  const { datetimeToClient } = useDate();
  const userNotifications = useUserNotifications();
  const tabsActionRef = useRef(null);

  const [anchorEl, setAnchorEl] = useState(null);
  const [mentions, setMentions] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState("mentions");

  const updateTabsIndicator = useCallback(() => {
    window.requestAnimationFrame(() => {
      if (
        tabsActionRef.current &&
        typeof tabsActionRef.current.updateIndicator === "function"
      ) {
        tabsActionRef.current.updateIndicator();
      }
    });
  }, []);

  const fetchMentions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await userNotifications.list({
        pageNumber: 1,
        pageSize: 100,
        onlyUnread: true,
      });
      setMentions(data?.notifications || []);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [userNotifications]);

  const fetchUnread = useCallback(async () => {
    try {
      const data = await userNotifications.unreadCount();
      setUnread(Number(data?.count) || 0);
    } catch {
      /* silent */
    }
  }, [userNotifications]);

  useEffect(() => {
    fetchUnread();
  }, [fetchUnread]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);
    if (!socket) return () => {};

    const event = `company-${companyId}-user-notification`;
    const handler = (payload) => {
      if (!payload) return;
      if (payload.action === "create" && payload.notification) {
        setMentions((prev) => [payload.notification, ...prev].slice(0, 50));
        setUnread((c) => c + 1);
      } else if (payload.action === "update" && payload.notification) {
        if (payload.notification.readAt) {
          setMentions((prev) =>
            prev.filter((n) => n.id !== payload.notification.id)
          );
        } else {
          setMentions((prev) =>
            prev.map((n) =>
              n.id === payload.notification.id ? payload.notification : n
            )
          );
        }
        fetchUnread();
      } else if (payload.action === "read-all") {
        setMentions([]);
        setUnread(0);
      }
    };

    socket.on(event, handler);
    return () => {
      try {
        socket.off(event, handler);
      } catch {
        /* noop */
      }
    };
  }, [socketManager, fetchUnread]);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    fetchMentions();
  };

  const handleClose = () => setAnchorEl(null);

  const handleMentionClick = async (item) => {
    try {
      if (!item.readAt) {
        await userNotifications.markAsRead(item.id);
        setUnread((c) => Math.max(0, c - 1));
      }
      setMentions((prev) => prev.filter((n) => n.id !== item.id));
    } catch {
      /* silent */
    }
    const payload = item.payload || {};
    handleClose();
    const chatUuid = payload.chatUuid || payload.chat?.uuid || payload.chat?.chatUuid;
    if (chatUuid) {
      history.push(`/chats/${chatUuid}`);
    } else if (payload.kanbanCardId) {
      history.push("/kanban", {
        openMentionNotification: true,
        mentionNotification: {
          kanbanCardId: payload.kanbanCardId,
          kanbanBoardId: payload.kanbanBoardId || null,
          ticketId: payload.ticketId || null,
        },
      });
    } else if (payload.ticketId) {
      history.push(`/tickets/${payload.ticketId}`);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await userNotifications.markAllAsRead();
      setMentions([]);
      setUnread(0);
    } catch {
      /* silent */
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? "mentions-popover" : undefined;
  const mentionUnread = mentions.filter(
    (item) => item.type === "mention" && !item.readAt
  ).length;
  const followingUnread = mentions.filter(
    (item) => isFollowNotification(item) && !item.readAt
  ).length;

  useEffect(() => {
    if (open) updateTabsIndicator();
  }, [open, activeFilter, mentionUnread, followingUnread, updateTabsIndicator]);

  const filteredNotifications = mentions.filter((item) => {
    if (activeFilter === "mentions") return item.type === "mention";
    if (activeFilter === "following") return isFollowNotification(item);
    return false;
  });
  const renderTabLabel = (label, count) => (
    <Badge
      badgeContent={count}
      color="secondary"
      invisible={count === 0}
      className={classes.tabBadge}
      max={99}
    >
      <span className={classes.tabLabel}>{label}</span>
    </Badge>
  );

  return (
    <>
      <Tooltip title="Central pessoal">
        <IconButton
          aria-describedby={id}
          className={classes.iconButton}
          onClick={handleOpen}
        >
          <Badge
            badgeContent={unread}
            color="secondary"
            invisible={unread === 0}
            className={classes.badge}
            max={99}
          >
            <AlternateEmailOutlinedIcon className={classes.iconButtonLabel} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        classes={{ paper: classes.popoverPaper }}
        TransitionProps={{ onEntered: updateTabsIndicator }}
      >
        <Box className={classes.header}>
          <Typography className={classes.headerTitle}>
            <AlternateEmailOutlinedIcon fontSize="small" />
            Central pessoal
          </Typography>
        </Box>
        <Tabs
          action={tabsActionRef}
          value={activeFilter}
          onChange={(_, value) => setActiveFilter(value)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          className={classes.tabs}
        >
          <Tab value="mentions" label={renderTabLabel("Menções", mentionUnread)} />
          <Tab
            value="following"
            label={renderTabLabel("Seguindo", followingUnread)}
          />
        </Tabs>

        <List className={classes.list}>
          {loading && mentions.length === 0 ? (
            <Box className={classes.loading}>
              <CircularProgress size={24} />
            </Box>
          ) : filteredNotifications.length === 0 ? (
            <Box className={classes.empty}>
              {activeFilter === "following" ? (
                <PersonPinRoundedIcon className={classes.emptyIcon} />
              ) : (
                <AlternateEmailOutlinedIcon className={classes.emptyIcon} />
              )}
              <Typography variant="body2">
                {activeFilter === "following"
                  ? "Nenhuma notificação de itens seguidos"
                  : i18n.t("mainDrawer.appBar.internalComms.noMentions") ||
                    "Nenhuma menção"}
              </Typography>
            </Box>
          ) : (
            filteredNotifications.map((item) => {
              const isUnread = !item.readAt;
              return (
                <ListItem
                  key={item.id}
                  button
                  className={`${classes.item} ${
                    isUnread ? classes.unread : ""
                  }`}
                  onClick={() => handleMentionClick(item)}
                >
                  <Box className={classes.content}>
                    <UserAvatar user={item.actor} className={classes.avatar} />
                    <Box className={classes.info}>
                      <Box className={classes.headerRow}>
                        <Typography className={classes.title}>
                          <span className={classes.typeLabel}>
                            {getNotificationTypeLabel(item)}
                          </span>
                          {item.actor?.name || "Sistema"}
                        </Typography>
                        <span className={classes.time}>
                          <AccessTimeIcon style={{ fontSize: 12 }} />
                          {datetimeToClient(item.createdAt)}
                        </span>
                      </Box>
                      <Typography className={classes.body}>
                        {stripMentionMarkers(item.body) || item.type}
                      </Typography>
                    </Box>
                  </Box>
                </ListItem>
              );
            })
          )}
        </List>

        {mentions.length > 0 && unread > 0 && (
          <Box className={classes.footer}>
            <Button
              fullWidth
              size="small"
              onClick={handleMarkAllRead}
              startIcon={<DoneAllIcon />}
            >
              {i18n.t("mainDrawer.appBar.internalComms.markAllRead") ||
                "Marcar todas como lidas"}
            </Button>
          </Box>
        )}
      </Popover>
    </>
  );
};

export default MentionsPopOver;
